import {
  useMutation,
  UseMutationResult,
  useQueryClient
} from '@tanstack/react-query';

import { useCustomAlerts } from '@/shared/lib/alert';
import {
  invalidateBookmarkFolders,
  patchBookmarkFolderCounts
} from '@/shared/lib/invalidateBookmarkFolders';
import { useAppSelector } from '@/shared/lib/redux';

import { API_URLS } from '@/shared/api/main';

import { bookmarkFavoriteUser } from '../api/bookmarkFavoriteUser';
import type { ITopUsersResponse } from './types/User';

interface IBookmarkMutationParams {
  id: number;
  action: 'add' | 'remove';
  /**
   * При `action: 'add'` — папки, в которые сразу положить юзера вместе с
   * добавлением (в т.ч. повтор по «Вернуть» из снекбара). При
   * `action: 'remove'` — папки, в которых юзер состоял ДО снятия: сервер
   * каскадом снимает привязки к папкам вместе с общим избранным, а клиент по
   * этим `folderIds` синхронно правит счётчики в блоке «Мои папки», не дожидаясь
   * инвалидации
   */
  folderIds?: number[];
}

/**
 * Мутация для добавления/удаления юзера в избранном
 */
export const useBookmarkFavoriteUserMutation = (): UseMutationResult<
  unknown,
  Error,
  IBookmarkMutationParams
> => {
  const { showCustomAlert } = useCustomAlerts();
  const queryClient = useQueryClient();
  const currentUserId = useAppSelector(state => state.user.data.id);

  return useMutation({
    /**
     * Мутация добавления/удаления юзера в избранное
     * @param root0 - пропсы
     * @param root0.id - id юзера
     * @param root0.action - тип действия 'add' | 'remove'
     * @param root0.folderIds - папки для одновременного добавления
     */
    mutationFn: ({ id, action, folderIds }: IBookmarkMutationParams) =>
      bookmarkFavoriteUser(id, action, folderIds),
    /**
     * Отображение ошибки мутации
     * @param error Ошибка
     */
    onError: error => {
      showCustomAlert(error.message, { severity: 'error' });
    },
    /**
     * При удачном обновлении юзера в избранном
     * @param _data - ответ сервера
     * @param variables - параметры мутации (id, action)
     */
    onSuccess: async (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [API_URLS.bookmarks.list.users],
        exact: false
      });
      queryClient.setQueryData<ITopUsersResponse>([API_URLS.users.top], old => {
        if (!old?.users) return old;
        return {
          ...old,
          users: old.users.map(user =>
            user.id === variables.id
              ? { ...user, bookmarked: variables.action === 'add' }
              : user
          )
        };
      });
      // Синхронно — до инвалидации: снятие сердечка каскадом снимает привязки к
      // папкам на сервере, и счётчики в «Моих папках» обязаны упасть в тот же
      // момент, что и сердце на карточке; «Вернуть» с folderIds — зеркально вверх
      const delta = variables.action === 'add' ? 1 : -1;
      patchBookmarkFolderCounts(queryClient, currentUserId, {
        folderIds: variables.folderIds ?? [],
        itemsDelta: delta,
        totalBookmarksDelta: delta
      });
      invalidateBookmarkFolders(queryClient, currentUserId);
    }
  });
};
